// src/components/PermissionGuard.js
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated, userHasRole, getLogoutRedirectUrl } from '../utils/authUtils';

const PermissionGuard = ({
  children,
  requiredRole = 'Admin',
  fallback = null,
  redirectTo = '/'
}) => {
  const [hasPermission, setHasPermission] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;

    const checkPermission = async () => {
      // Check authentication first
      if (!isAuthenticated()) {
        console.log('[PermissionGuard] User not authenticated, redirecting to login');

        // Redirect to login page instead of home page
        const loginUrl = getLogoutRedirectUrl();
        window.location.href = loginUrl;

        if (isMounted) {
          setHasPermission(false);
          setIsChecking(false);
        }
        return;
      }

      try {
        const roleToCheck = requiredRole || 'Admin';
        const permitted = await userHasRole(roleToCheck);

        if (!isMounted) return;

        setHasPermission(permitted);
        setIsChecking(false);

        if (!permitted && redirectTo) {
          navigate(redirectTo);
        }
      } catch (error) {
        console.error('[PermissionGuard] Permission check failed:', error);
        if (!isMounted) return;

        setHasPermission(false);
        setIsChecking(false);

        if (redirectTo) {
          navigate(redirectTo);
        }
      }
    };

    checkPermission();

    return () => {
      isMounted = false;
    };
  }, [navigate, redirectTo, requiredRole]);

  if (isChecking) {
    return (
      <div className="main-section">
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <p>Checking permissions...</p>
        </div>
      </div>
    );
  }

  if (!hasPermission) {
    return fallback || (
      <div className="main-section">
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  return children;
};

export default PermissionGuard;
